import axios from "axios";
import api from "../../utils/api";
import { mockQuickDaily, mockUnits } from "./quickReportsMock";
import { unitsFromDevices, withTimeout, type UnitRef } from "./quickReports.compute";
import { fetchAllDevices } from "../../features/devices/queries";
import type { QuickDailyParams, QuickDailyResponse } from "./types";
import { hasHosLimits, NO_HOS_LIMITS, readHosLimits, type HosLimits } from "./bitacora/hos";

const MOCK_FORCED = import.meta.env.VITE_QUICK_REPORTS_MOCK === "true";

// Fuente única de los informes rápidos. Sólo en desarrollo, mientras el backend
// no publique /reports/quick/daily (404), se usa el mock; la respuesta trae
// `mock: true` y la página lo avisa con una franja "Datos de ejemplo". En un
// build de producción un 404 es error: un cliente nunca debe ver datos inventados.
// `timeoutMs`: la bitácora y sus relojes cortan a los 15 s para mostrar
// "Reintentar" en vez de un esqueleto; el informe de 15 días de toda la flota
// se queda con el plazo general del API (30 s), porque puede tardar de verdad.
export function fetchQuickDaily(params: QuickDailyParams, signal?: AbortSignal, timeoutMs?: number): Promise<QuickDailyResponse> {
  return timeoutMs ? withTimeout((s) => request(params, s), timeoutMs, signal) : request(params, signal);
}

async function request(params: QuickDailyParams, signal?: AbortSignal): Promise<QuickDailyResponse> {
  if (MOCK_FORCED) return mockQuickDaily(params);
  try {
    const { data } = await api.get("/reports/quick/daily", {
      signal,
      params: {
        companyId: params.companyId,
        from: params.from,
        to: params.to,
        tz: params.tz,
        ...(params.deviceIds?.length ? { deviceIds: params.deviceIds.join(",") } : {}),
      },
    });
    // Tolera el sobre { success, data } que usan otros endpoints del API.
    const body = (data?.items ? data : data?.data) as QuickDailyResponse | undefined;
    return { ...(body as QuickDailyResponse), items: Array.isArray(body?.items) ? body.items : [] };
  } catch (err) {
    if (import.meta.env.DEV && axios.isAxiosError(err) && err.response?.status === 404) return mockQuickDaily(params);
    throw err;
  }
}

// ---------- Límites de horas de servicio (companies.hos_limits) ----------

// Con el mock forzado no hay sesión contra el API: los límites viven en memoria
// para poder probar el modal sin tocar una compañía real.
let mockHosLimits: HosLimits = NO_HOS_LIMITS;

export async function fetchHosLimits(companyId: string, signal?: AbortSignal): Promise<HosLimits> {
  if (MOCK_FORCED) return mockHosLimits;
  const { data } = await api.get("/company", { params: { id: companyId }, signal });
  return readHosLimits((data?.data ?? data)?.hos_limits);
}

// Todo vacío se manda como null: la compañía vuelve a "sólo calcular".
export async function saveHosLimits(companyId: string, limits: HosLimits): Promise<HosLimits> {
  const hos_limits = hasHosLimits(limits) ? limits : null;
  if (MOCK_FORCED) return (mockHosLimits = readHosLimits(hos_limits));
  const { data } = await api.put("/company", { hos_limits }, { params: { id: companyId } });
  return readHosLimits((data?.data ?? data)?.hos_limits);
}

// Unidades de la compañía para la bitácora, sin pedir el resumen de la flota.
export async function fetchQuickUnits(companyId: string, signal?: AbortSignal): Promise<UnitRef[]> {
  if (MOCK_FORCED) return unitsFromDevices(mockUnits(companyId).map((u) => ({ id: u.device_id, vehicle: { name: u.unit_name, plate: u.plate } })));
  return unitsFromDevices(await fetchAllDevices(companyId, signal));
}
